import { canSaveBoardAsTemplate, templateEntriesFromBoard } from '../file';
import { ok, reject } from '../result';
import type {
  Decision,
  SaveBoardAsTemplateCommand,
  SaveBoardAsTemplateSnapshot,
  TemplateOp,
} from './types';

/**
 * PRE-6 — *"Any match's board is savable as a named user template post-match."* (build unit U-16)
 *
 * ── Why this is its own module and not a fifth handler in `setup.ts` ─────────
 *
 * `commands/setup.ts` owns PRE-1 … PRE-7 and already writes templates (PRE-2's seed install), so it
 * is the obvious home. It is the wrong one, for the seam this codebase uses everywhere else —
 * **what the handler is allowed to write**. Every handler in `setup.ts` declares
 * `Decision<SetupOp>`, which carries `insertTeam`, `insertPlayer`, `insertTemplate` and
 * `patchMatchSetup`; a PRE-6 handler declaring that type could edit the setup of the match it is
 * copying, and the only thing stopping it would be that it does not happen to. Declaring
 * {@link TemplateOp} instead makes *"saving a template changes nothing about the match"* a compile
 * error rather than a review finding — the same argument `halftimeManage.ts` makes for splitting
 * itself out of `halftime.ts`.
 *
 * The **window** is the other half. `setup.ts`'s handlers all guard on `preMatch`
 * (`ERR_SETUP_NOT_PERMITTED`); this one guards on the exact complement (§6.0: *"Not available during
 * an in-match state — it is a post-match action"*). Two opposite windows in one module is how a
 * later reader ends up applying the wrong one.
 */

const MODULE = 'commands/template';

/**
 * PRE-6 — creates a `builtin = false` `Template` from the match's positioned hypotheses.
 *
 * ── The row is built here, from the snapshot ────────────────────────────────
 *
 * See {@link SaveBoardAsTemplateCommand}. The caller supplies the id and the name; the entries come
 * from `templateEntriesFromBoard` over rows read **inside** the transaction, so what is saved is
 * what the board holds at commit rather than what the screen was showing when the sheet opened.
 * That matters on this surface specifically: the Team view lists a `fullTime` match beside a
 * *Distill* action, and distillation can add a `Hypothesis` (DIST-1's promote path) between the
 * user opening the name sheet and tapping save.
 *
 * ── `phase: null` on the row, deliberately ─────────────────────────────────
 *
 * §3.1: *"Optional grouping label. Entry-level `phase` is what actually classifies a hypothesis."*
 * A saved board spans up to eight patterns across seven phases, so there is no one label that is
 * true of it — and picking the first entry's phase would file a whole board under whichever pattern
 * the user happened to choose first.
 */
export function handleSaveBoardAsTemplate(
  cmd: SaveBoardAsTemplateCommand,
  snap: SaveBoardAsTemplateSnapshot,
): Decision<TemplateOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  /**
   * §6.0's negative. Read inside the transaction, so a Full Time that has not yet committed cannot
   * open this window early and a match that moved on cannot keep it open — the rendered absence of
   * the control on a running match is what the user sees, and this is what makes it true.
   */
  if (!canSaveBoardAsTemplate(match.state)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_TEMPLATE_NOT_POST_MATCH',
      'A board is saved as a template after the match, not during one.',
      { state: match.state },
    );
  }

  const name = cmd.name.trim();
  if (name === '') {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_TEMPLATE_NAME_REQUIRED',
      'Give the template a name so you can find it next time.',
      { templateId: cmd.templateId },
    );
  }

  const hypothesisTexts = templateEntriesFromBoard(snap.hypotheses);
  if (hypothesisTexts.length === 0) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_TEMPLATE_EMPTY',
      'This match has no board to save — nothing was ever positioned on it.',
      { hypothesisCount: snap.hypotheses.length },
    );
  }

  // A `put` under an existing id would overwrite a library entry the user may have spent a season
  // building. `insertTemplate` uses `add` and would throw, but a thrown `ConstraintError` aborts the
  // transaction with a storage message; this returns a code the sheet can render.
  if (snap.existingTemplateIds.includes(cmd.templateId)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_TEMPLATE_EXISTS',
      'That template already exists.',
      { templateId: cmd.templateId },
    );
  }

  return ok({
    ops: [
      {
        t: 'insertTemplate',
        row: {
          id: cmd.templateId,
          name,
          phase: null,
          hypothesisTexts,
          // PRE-2 reserves `true` for seeded content, which the first-run installer owns and BAK-2's
          // import round-trips. A user template is never built-in.
          builtin: false,
        },
      },
    ],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: 'PRE-6: board saved as a user template',
        data: {
          templateId: cmd.templateId,
          entries: hypothesisTexts.length,
          state: match.state,
        },
      },
    ],
    post: [],
  });
}
